/**
 * Pseudo-enums (const objects) for Prisma enums
 * These replace TypeScript enums to avoid the issues described in:
 * https://dev.to/ivanzm123/dont-use-enums-in-typescript-they-are-very-dangerous-57bh
 *
 * Pattern: const object with 'as const' + type alias
 */

export const Status = {
    PENDING: 'PENDING',
    ACTIVE: 'ACTIVE',
    DISABLED: 'DISABLED',
    DELETED: 'DELETED',
} as const;

export type Status = keyof typeof Status;

export const CredentialType = {
    PHYSICAL_TERMINAL: 'PHYSICAL_TERMINAL',
    VIRTUAL_TERMINAL: 'VIRTUAL_TERMINAL',
    IPX: 'IPX',
} as const;

export type CredentialType = keyof typeof CredentialType;

export const IpxCredentialDefinitionKind = {
    WEB_LOGIN_WORKFLOW: 'WEB_LOGIN_WORKFLOW',
    MACHINE_AUTH: 'MACHINE_AUTH',
} as const;

export type IpxCredentialDefinitionKind =
    keyof typeof IpxCredentialDefinitionKind;

export const CredentialRotationAttemptState = {
    CREATED: 'CREATED',
    INJECTED: 'INJECTED',
    SUBMITTED: 'SUBMITTED',
    PROMOTED: 'PROMOTED',
    PROMOTION_FAILED: 'PROMOTION_FAILED',
    FAILED: 'FAILED',
    EXPIRED: 'EXPIRED',
    ABANDONED: 'ABANDONED',
} as const;

export type CredentialRotationAttemptState =
    keyof typeof CredentialRotationAttemptState;

export const InternalRole = {
    SUPERADMIN: 'SUPERADMIN',
    INTERNAL_ADMIN: 'INTERNAL_ADMIN',
} as const;

export type InternalRole = keyof typeof InternalRole;

export const OrganizationalRole = {
    PRINCIPAL: 'PRINCIPAL',
    ADMIN: 'ADMIN',
    REP: 'REP',
} as const;
export type OrganizationalRole = keyof typeof OrganizationalRole;

export const Role = {
    ...InternalRole,
    ...OrganizationalRole,
} as const;
export type Role = keyof typeof Role;

export const AllInternalRoles: Role[] = Object.values(InternalRole);
export const AllOrganizationalRoles: Role[] = Object.values(OrganizationalRole);
export const AllInternalOnly: InternalRole[] = Object.values(InternalRole);
export const AllOrganizationalOnly: OrganizationalRole[] = Object.values(OrganizationalRole);
export const AllRoles: Role[] = Object.values(Role);

export const AllAdminRoles: Role[] = [
    Role.SUPERADMIN,
    Role.INTERNAL_ADMIN,
    Role.PRINCIPAL,
    Role.ADMIN,
];
export type AdminRole = (typeof AllAdminRoles)[number];

export const RoleRankInternal = {
    SUPERADMIN: 100,
    INTERNAL_ADMIN: 90,
} as const; // Values are arbitrary and not be relied on, but the order is guaranteed to be consistent
export const RoleRankOrganizational = {
    PRINCIPAL: 80,
    ADMIN: 70,
    REP: 50,
} as const; // Values are arbitrary and not be relied on, but the order is guaranteed to be consistent

export const RoleRank = {
    ...RoleRankInternal,
    ...RoleRankOrganizational,
} as const;

export const isInternalRole = (role: Role): role is InternalRole => AllInternalRoles.includes(role);
export const isOrganizationalRole = (role: Role): role is OrganizationalRole =>
    AllOrganizationalRoles.includes(role);
export const isAdminRole = (role: Role): role is AdminRole => AllAdminRoles.includes(role);

export const UserType = {
    INTERNAL: 'internal',
    ORGANIZATIONAL: 'organizational',
    // CUSTOMER: 'customer',
} as const;
export type UserType = (typeof UserType)[keyof typeof UserType];

export const EmailTokenKind = {
    INVITATION: 'INVITATION',
    PASSWORD_RESET: 'PASSWORD_RESET',
    EMAIL_VERIFICATION: 'EMAIL_VERIFICATION',
    SMS_VERIFICATION: 'SMS_VERIFICATION',
    PAYMENT_REQUEST_ACCESS: 'PAYMENT_REQUEST_ACCESS',
    PILLAR_LOGIN_LINK: 'PILLAR_LOGIN_LINK',
} as const;

export type EmailTokenKind = (typeof EmailTokenKind)[keyof typeof EmailTokenKind];

export const Operation = {
    INSERT: 'INSERT',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
} as const;

export type Operation = keyof typeof Operation;

export const TableName = {
    USERS: 'users',
    ORGANIZATIONS: 'organizations',
    MERCHANT_CUSTOMER_RECORDS: 'merchant_customer_records',
    ROLE_ASSIGNMENTS: 'role_assignments',
    SINGLE_USE_AUTH_TOKENS: 'single_use_auth_tokens',
    TOTP_BACKUP_CODES: 'totp_backup_codes',
    IPW_CUSTOMER_LINKS: 'ipw_customer_links',
    PAYMENT_METHOD_TOKENS: 'payment_method_tokens',
    STORED_PAYMENT_METHODS: 'stored_payment_methods',
    CREDIT_CARD_STORED_PAYMENT_DETAILS: 'credit_card_stored_payment_details',
    DEBIT_CARD_STORED_PAYMENT_DETAILS: 'debit_card_stored_payment_details',
    ACH_STORED_PAYMENT_DETAILS: 'ach_stored_payment_details',
    PAYMENT_REQUESTS: 'payment_requests',
    PAYMENT_REQUEST_DELIVERY_EVENTS: 'payment_request_delivery_events',
    PAYMENT_REQUEST_DOCUMENTS: 'payment_request_documents',
    DOCUMENT_TEMPLATES: 'document_templates',
    PAYMENT_TRANSACTIONS: 'payment_transactions',
    PAYMENT_TRANSACTION_LIFECYCLE_EVENTS: 'payment_transaction_lifecycle_events',
    CREDENTIALS: 'credentials',
    PERMISSIONS_BUNDLES: 'permissions_bundles',
    PERMISSIONS_POLICY_GRANTS: 'permissions_policy_grants',
    LEGAL_DOCUMENTS: 'legal_documents',
    USER_LEGAL_DOCUMENT_ACCEPTANCES: 'user_legal_document_acceptances',
    PROCESSORS: 'processors',
    MERCHANT_ACCOUNTS: 'merchant_accounts',
    LOGICAL_TERMINALS: 'logical_terminals',
    TERMINAL_DEVICES: 'terminal_devices',
    ALLOWED_WEBSITE_TEMPLATES: 'allowed_website_templates',
    ORGANIZATION_ALLOWED_WEBSITES: 'organization_allowed_websites',
} as const;

export type TableName = (typeof TableName)[keyof typeof TableName];

export const OrganizationAllowedWebsiteSource = {
    LOCAL: 'LOCAL',
    TEMPLATE_REFERENCE: 'TEMPLATE_REFERENCE',
    TEMPLATE_COPY: 'TEMPLATE_COPY',
} as const;

export type OrganizationAllowedWebsiteSource =
    (typeof OrganizationAllowedWebsiteSource)[keyof typeof OrganizationAllowedWebsiteSource];

export const PaymentTransactionStatus = {
    INFLIGHT: 'INFLIGHT', // Light blue: we don't know yet if the GW received our request
    ACCEPTED: 'ACCEPTED', // Dark blue:  the GW confirmed receipt of our request
    DECLINED: 'DECLINED', // Purple: the GW explicitly declined authorization
    AUTHORIZED: 'AUTHORIZED', // Light green: the GW has authorized the transaction
    BATCHED: 'BATCHED', // Dark green: the GW has batched the transaction
    DEPOSITED: 'DEPOSITED', // Tan: the processor has deposited the funds on our account
    FAILED: 'FAILED', // Red: the GW rejected the transaction request
    VOIDED: 'VOIDED', // Pink: the transaction was voided by our request
    REFUNDED: 'REFUNDED', // Magenta: we refunded the customer, partially or fully
} as const;
export type PaymentTransactionStatus = keyof typeof PaymentTransactionStatus;
export const AllPaymentTransactionStatuses: PaymentTransactionStatus[] =
    Object.values(PaymentTransactionStatus);

export const PaymentReconciliationStatus = {
    NEW: 'NEW',
    RECONCILED: 'RECONCILED',
    VERIFIED: 'VERIFIED',
    MUST_AMEND: 'MUST_AMEND',
    AMENDED: 'AMENDED',
    AMEND_VERIFIED: 'AMEND_VERIFIED',
} as const;
export type PaymentReconciliationStatus =
    (typeof PaymentReconciliationStatus)[keyof typeof PaymentReconciliationStatus];

export const PaymentTransactionLifecycleEventType = {
    SYNC_PAYMENT: 'SYNC_PAYMENT',
    VOID: 'VOID',
    REFUND: 'REFUND',
    GATEWAY_TRANSACTION_DETAILS: 'GATEWAY_TRANSACTION_DETAILS',
    RECONCILIATION: 'RECONCILIATION',
    BATCH: 'BATCH',
} as const;

export type PaymentTransactionLifecycleEventType =
    keyof typeof PaymentTransactionLifecycleEventType;

// GatewayId: Integer IDs for payment gateways (stored in DB to save space)
// IDs 0-1000 reserved for future PILLAR gateway
// WARNING: Never change existing IDs, only add new ones
export const GatewayId = {
    // Reserved: 0-1000 for PILLAR gateway (future)
    VALOR_US: 1001,
} as const;

export type GatewayId = (typeof GatewayId)[keyof typeof GatewayId];

// Reverse lookup: ID -> name (for display purposes)
export const GatewayIdToName: Record<GatewayId, string> = {
    [GatewayId.VALOR_US]: 'VALOR_US',
} as const;
